import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from "@/components/ui/popover"
import { Calendar } from "@/components/ui/calendar"
import { CalendarIcon, Filter } from "lucide-react"
import { format as formatDate } from "date-fns"
import { cn } from "@/lib/utils"
import { useState, useEffect } from "react"
import { useDebounce } from "@/hooks/useDebounce"

interface FilterBarProps {
  startDate: Date | undefined
  setStartDate: (date: Date | undefined) => void
  endDate: Date | undefined
  setEndDate: (date: Date | undefined) => void
  searchQuery: string
  setSearchQuery: (query: string) => void
}

export function FilterBar({ 
  startDate, 
  setStartDate,
  endDate,
  setEndDate,
  searchQuery,
  setSearchQuery
}: FilterBarProps) {
  // Local state for the search input
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery)
  
  // Debounce the search query to reduce excessive filtering
  const debouncedSearchQuery = useDebounce(localSearchQuery, 300)
  
  // Update parent component when debounced search query changes
  useEffect(() => {
    setSearchQuery(debouncedSearchQuery)
  }, [debouncedSearchQuery, setSearchQuery])
  
  // Sync local state with prop changes (for external resets)
  useEffect(() => {
    setLocalSearchQuery(searchQuery)
  }, [searchQuery])

  return (
    <div className="flex flex-col md:flex-row gap-4 items-start">
      <div className="flex flex-col w-full md:w-auto md:flex-row gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "justify-start text-left font-normal",
                !startDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {startDate ? (
                formatDate(startDate, "PPP")
              ) : (
                <span>Start Date</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={startDate}
              onSelect={setStartDate}
              initialFocus
              className="pointer-events-auto"
            />
          </PopoverContent>
        </Popover>

        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "justify-start text-left font-normal",
                !endDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {endDate ? (
                formatDate(endDate, "PPP")
              ) : (
                <span>End Date</span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={endDate}
              onSelect={setEndDate}
              initialFocus
              className="pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="relative flex-grow w-full md:w-auto">
        <Input
          placeholder="Filter by justification, status..."
          className="w-full pl-3 pr-10"
          value={localSearchQuery}
          onChange={(e) => setLocalSearchQuery(e.target.value)}
        />
        <Filter className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground" />
      </div>
    </div>
  )
}
